import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, orderBy, limit, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Topic gate panel (Content Queue page, per selected client)
//
// Three things staff need to see that the queue rows alone cannot show:
//   1. Held long-shot topics — titles the gate refused to queue because the
//      term has no demand and no local site ranks for it, or because national
//      authorities own the top ten. "Queue anyway" is the explicit override;
//      nothing here is ever written without it.
//   2. Starvation — the scheduler could not fill this client's queue without
//      long-shots. The gate is never relaxed to fix that; a human decides.
//   3. The site-authority flag with its diagnosis — most posts earn zero
//      clicks regardless of topic, so topic selection is not the lever.
// ---------------------------------------------------------------------------

const FN_BASE = 'https://us-central1-core6-marketing.cloudfunctions.net';

async function callFn(name, body) {
  const idToken = await auth.currentUser?.getIdToken();
  const res = await fetch(`${FN_BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function fmtWhen(ts) {
  const d = ts && ts.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
  return d && !isNaN(d) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

const label = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-muted)' };

// Text chip; the two tones differ in lightness, never hue alone.
export function TierChip({ tier, override }) {
  if (override) return <span style={{ ...chip, background: '#f8fafc', color: '#0f172a', border: '1px solid #0f172a' }}>Override</span>;
  if (!tier) return null;
  const t = { strong: ['Strong', '#1f2937', '#f8fafc', '#4b5563'], viable: ['Viable', 'transparent', 'var(--text-secondary)', 'var(--border)'], unscored: ['Unscored', 'transparent', 'var(--text-muted)', 'var(--border)'], 'long-shot': ['Long-shot', 'transparent', 'var(--text-muted)', 'var(--border)'] }[tier];
  if (!t) return null;
  return <span style={{ ...chip, background: t[1], color: t[2], border: `1px solid ${t[3]}` }}>{t[0]}</span>;
}
const chip = { fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', padding: '1px 7px', borderRadius: 20, whiteSpace: 'nowrap' };

export default function TopicGatePanel({ clientId, client, scheduleDays, nextAvailableDate, takenDateKeys, ptDateKey }) {
  const [held, setHeld] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({});

  useEffect(() => {
    if (!clientId) return undefined;
    const q = query(collection(db, 'topicCandidates'), where('clientId', '==', clientId), where('status', '==', 'held'), orderBy('createdAt', 'desc'), limit(50));
    return onSnapshot(q, snap => setHeld(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => setHeld([]));
  }, [clientId]);

  if (!client) return null;
  const auth_ = client.blogAuthority || null;
  const starved = client.topicGateStarved || null;

  const queueAnyway = async (c) => {
    setBusy(c.id); setMsg(null);
    try {
      const taken = new Set(takenDateKeys);
      const nextDate = nextAvailableDate(scheduleDays, taken);
      if (!nextDate) throw new Error('No open blog day in the scheduling window.');
      await addDoc(collection(db, 'contentQueue'), {
        clientId, clientName: client.businessName,
        title: c.title, keyword: c.keyword || '', articleType: c.articleType || 'Informational', language: 'en',
        wordCount: c.wordCount || 1500, status: 'planned', scheduledFor: nextDate.toISOString(), siteUrl: client.clientUrl || '',
        additionalDetails: (c.talkingPoints || []).map(p => `- ${p}`).join('\n'),
        persona: c.persona ?? null, generatedByAi: true,
        demandVolume: c.demandVolume ?? null, searchIntent: c.searchIntent ?? null, headKeyword: c.headKeyword ?? null,
        topicTier: c.topicTier, topicPattern: c.topicPattern, topicPatternEvidence: c.topicPatternEvidence ?? null,
        topicSourceQuestion: c.topicSourceQuestion ?? null, topicSourceVerified: !!c.topicSourceVerified,
        topicReasons: c.topicReasons || [], serpAuthorityCount: c.serpAuthorityCount ?? null, serpLocalCount: c.serpLocalCount ?? null,
        serpClientPresent: c.serpClientPresent ?? null, serpLocation: c.serpLocation ?? null, serpTopDomains: c.serpTopDomains || [],
        tierOverride: true, overriddenBy: auth.currentUser?.email || null, overriddenAt: serverTimestamp(),
        postId: null, postUrl: null, createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'topicCandidates', c.id), { status: 'queued', queuedBy: auth.currentUser?.email || null, queuedAt: serverTimestamp() });
      setMsg({ ok: true, text: `Queued for ${nextDate.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric' })} as an override.` });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy('');
  };

  const dismiss = async (c) => {
    setBusy(c.id);
    try { await updateDoc(doc(db, 'topicCandidates', c.id), { status: 'dismissed', dismissedBy: auth.currentUser?.email || null, dismissedAt: serverTimestamp() }); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy('');
  };

  const rerunAuthority = async () => {
    setBusy('authority'); setMsg(null);
    try {
      const r = await callFn('blogAuthorityRun', { clientId });
      setMsg({ ok: true, text: r.flagged ? `Flag stands: ${r.zeroClickShare}% of posts with zero clicks, ${r.clicksPerPost} clicks per post.${r.diagnosed ? ' Diagnosis updated.' : ''}` : `Not flagged: ${r.clicksPerPost ?? '—'} clicks per post.` });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy('');
  };

  const profile = client.topicProfile || null;

  const scanHooks = async () => {
    setBusy('hooks'); setMsg(null);
    try {
      const r = await callFn('regulatoryHookRun', { clientId, force: !profile });
      const q = (r.queued || []).map(x => `queued "${x.title}" for ${x.scheduledFor ? new Date(x.scheduledFor).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric' }) : 'next slot'}${x.displaced ? ` (bumped "${x.displaced}" to the end)` : ''}`);
      const h = (r.held || []).map(x => `held "${x.title}": ${x.why}`);
      setMsg({ ok: true, text: r.skipped && typeof r.skipped === 'string' ? `Skipped: ${r.skipped}` : (r.found === 0 ? 'No real rule, rate, or permit changes found in the last 60 days.' : [...q, ...h].join(' · ')) });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy('');
  };

  const saveProfile = async () => {
    setBusy('profile'); setMsg(null);
    try {
      await callFn('topicProfileSave', { clientId, profile: profileDraft });
      setEditingProfile(false);
      setMsg({ ok: true, text: 'Topic profile saved.' });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy('');
  };

  const startEdit = () => {
    setProfileDraft({
      vertical: profile?.vertical || '', services: (profile?.services || []).join(', '), city: profile?.city || '', county: profile?.county || '',
      state: profile?.state || '', regulatoryDomains: (profile?.regulatoryDomains || []).join(', '), customerType: profile?.customerType || '',
    });
    setEditingProfile(true);
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      {msg && <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: msg.ok ? 'var(--text-primary)' : '#f87171' }}>{msg.text}</div>}

      {/* ── Topic profile + regulatory hook scan ─────────────────────── */}
      <div style={{ marginBottom: (held.length || starved || (auth_ && auth_.flagged)) ? 18 : 0, paddingBottom: (held.length || starved || (auth_ && auth_.flagged)) ? 16 : 0, borderBottom: (held.length || starved || (auth_ && auth_.flagged)) ? '1px solid var(--border)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <span style={label}>Topic profile · </span>
            {profile && profile.vertical
              ? <>{profile.vertical}{profile.city || profile.state ? ` · ${[profile.city, profile.state].filter(Boolean).join(', ')}` : ''}{(profile.regulatoryDomains || []).length ? <span style={{ color: 'var(--text-muted)' }}> · watching: {profile.regulatoryDomains.join(', ')}</span> : null}</>
              : <span style={{ color: 'var(--text-muted)' }}>not derived yet, the first hook scan builds it from the brand kit and service pages</span>}
            {profile && profile.lastHookScanAt && <span style={{ color: 'var(--text-muted)' }}> · last hook scan {fmtWhen(profile.lastHookScanAt)}{profile.lastHookScanResult ? ` (${profile.lastHookScanResult.found} found, ${profile.lastHookScanResult.queued} queued, ${profile.lastHookScanResult.held} held)` : ''}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {profile && profile.vertical && !editingProfile && <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={startEdit}>Edit</button>}
            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={scanHooks} disabled={busy !== ''} title="Search for rule, code, permit, rebate, or rate changes in the last 60 days. A hook verified by two independent sources takes the next open blog day.">
              {busy === 'hooks' ? 'Scanning…' : 'Scan for regulatory hooks'}
            </button>
          </div>
        </div>
        {editingProfile && (
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            {[['vertical', 'Trade / vertical'], ['customerType', 'Customer type'], ['city', 'City'], ['county', 'County'], ['state', 'State (2 letters)'], ['services', 'Services (comma separated)'], ['regulatoryDomains', 'Rule areas to watch (comma separated)']].map(([k, l]) => (
              <div className="form-field" key={k} style={{ marginBottom: 0, gridColumn: k === 'services' || k === 'regulatoryDomains' ? '1 / -1' : undefined }}>
                <label style={{ fontSize: 11 }}>{l}</label>
                <input type="text" value={profileDraft[k]} onChange={e => setProfileDraft(d => ({ ...d, [k]: e.target.value }))} />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={saveProfile} disabled={busy !== ''}>{busy === 'profile' ? 'Saving…' : 'Save profile'}</button>
              <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setEditingProfile(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {starved && (
        <div style={{ marginBottom: 16, padding: '10px 14px', border: '1px solid #0f172a', borderRadius: 8, fontSize: 13 }}>
          <strong>Queue not filled.</strong> The last automatic top-up ({fmtWhen(starved.at)}) wanted {starved.wanted} titles and only {starved.returned} cleared the gate; {starved.held} were held as long-shots. The gate is not relaxed to fill a queue. Override a held topic below, add titles by hand, or accept fewer posts.
        </div>
      )}

      {auth_ && auth_.flagged && (
        <div style={{ marginBottom: held.length ? 20 : 0, padding: '12px 14px', border: '1px solid #0f172a', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13 }}>
              <strong>Site authority concern.</strong> Over {auth_.period}, {auth_.zeroClickShare}% of this client's posts with Search Console data earned zero clicks ({auth_.clicksPerPost} clicks per published post; {auth_.deepShare}% sit below position 15). Topic selection will not fix this.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {auth_.diagnosis && <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setShowDiag(s => !s)}>{showDiag ? 'Hide diagnosis' : 'Show diagnosis'}</button>}
              <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={rerunAuthority} disabled={busy !== ''}>{busy === 'authority' ? 'Running…' : 'Re-run'}</button>
            </div>
          </div>
          {showDiag && auth_.diagnosis && (
            <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 10 }}>{auth_.diagnosis.summary}</div>
              <div style={{ ...label, marginBottom: 6 }}>Likely causes · primary: {String(auth_.diagnosis.primary_cause || '').replace(/_/g, ' ')}</div>
              {(auth_.diagnosis.causes || []).map((c, i) => (
                <div key={i} style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                  <div><strong>{c.cause}</strong> <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({c.confidence} confidence)</span></div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{c.evidence}</div>
                </div>
              ))}
              <div style={{ ...label, margin: '14px 0 6px' }}>Suggestions</div>
              {(auth_.diagnosis.suggestions || []).map((s, i) => (
                <div key={i} style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                  <div><strong>{s.action}</strong> <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {s.effort} effort · {s.owner === 'client' ? 'client' : 'Core6'}</span></div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{s.why}</div>
                </div>
              ))}
              {auth_.diagnosedAt && <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>Diagnosed {fmtWhen(auth_.diagnosedAt)}.</div>}
            </div>
          )}
        </div>
      )}

      {held.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={label}>Held as long-shots ({held.length})</div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 10 }}>
            The generator proposed these and the gate refused to queue them: no measurable demand and no local site ranking for the term, or a top ten owned by national authorities. Each carries its full brief. Queueing one is an explicit override and is recorded as such.
          </div>
          {held.map(c => {
            const open = expanded === c.id;
            return (
              <div key={c.id} style={{ borderTop: '1px solid var(--border)', padding: '8px 0' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setExpanded(open ? null : c.id)}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {c.headKeyword || c.keyword}{c.demandVolume != null ? ` · ${c.demandVolume}/mo` : ' · no volume data'}{c.serpAuthorityCount != null ? ` · top 10: ${c.serpAuthorityCount} authority, ${c.serpLocalCount} local` : ''}{c.serpLocation ? ` · ${c.serpLocation}` : ''} · {fmtWhen(c.createdAt)}
                    </div>
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} disabled={busy !== ''} onClick={() => queueAnyway(c)}>{busy === c.id ? '…' : 'Queue anyway'}</button>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11, opacity: 0.7 }} disabled={busy !== ''} onClick={() => dismiss(c)}>Dismiss</button>
                </div>
                {open && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    <div style={{ marginBottom: 6 }}>{(c.topicReasons || []).map((r, i) => <div key={i}>· {r}</div>)}</div>
                    {(c.serpTopDomains || []).length > 0 && <div style={{ marginBottom: 6, fontSize: 11, color: 'var(--text-muted)' }}>Top 10: {c.serpTopDomains.join(' · ')}</div>}
                    {(c.talkingPoints || []).length > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{c.talkingPoints.map(p => `- ${p}`).join('\n')}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
